// @flow
import React, { PureComponent } from 'react';
import { Redirect } from 'react-router-dom';

import { withHelmet, MAX_DESCRIPTION_LENGTH } from 'infrastructure/seo/Helmet';
import Breadcrumb from 'components/fragments/Breadcrumb';
import ArticleHeader from 'components/newsletter/ArticleHeader';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import ArticleSocial from 'components/newsletter/ArticleSocial';
import ArticleTags from 'components/newsletter/ArticleTags';
import FooterSocial from 'components/footer/FooterSocial';
import { makeAmpLink } from 'constants/app';
import { STATUS_FAILURE, STATUS_UNKNOWN } from 'constants/statusConstants';
import Sidebar from 'components/sidebar/Sidebar';
import { getDateFromString } from 'utils/dateUtils';
import { getFirstBlocText } from 'utils/blocUtils'; 
import type { ArticleType } from 'types/Article';
import type { Status } from 'types/Status';
import type { BlocContent } from 'types/NewsletterBloc';
import { handleTagEvent } from 'utils/tagManagerUtils';
import { tagsUtils } from 'utils/tagsUtils';

export type OwnProps = {
  match: {
    params: {
      slug: string
    }
  },
  location: {
    pathname: string
  }
};

export type StateProps = {
  article: ArticleType,
  articles: ArticleType[],
  status: Status
};

export type DispatchProps = {
  fetchArticle: (slug: string) => void,
  articleFetched: (article: ArticleType) => void
};

type State = {
  shouldDisplayLoading: boolean
};

type Props = OwnProps & StateProps & DispatchProps;

class Article extends PureComponent<Props, State> {
  props: Props;
  _articleContent: ?HTMLElement;
  _tags;

  componentDidMount() {
    const cachedArticle = this.props.articles.find(article => article.slug === this.props.match.params.slug);
    if (cachedArticle) {
      this.props.articleFetched(cachedArticle);
    }

    this.props.fetchArticle(this.props.match.params.slug);
  }

  componentWillUnmount() {
    if (this._tags) {
      this._tags.removeTagsEvents();
    }
  }

  componentDidUpdate() {
    if (this._articleContent) {
      this._tags = tagsUtils(this._articleContent);
    }
  }

  clickTagManager = (socialName: string) => {
    const { title } = this.props.article;
    return handleTagEvent('partage', title, socialName);
  };

  renderTrueBlocs = (blocs: Array<BlocContent>): any => {
    return blocs.map((blocFigure, index) => <ContentBloc key={index} {...blocFigure} />);
  };

  render() {
    const {
      label,
      caption,
      title,
      illustration,
      illustration_video,
      blocs,
      tags,
      sidebar
    } = this.props.article;
    const date = getDateFromString(this.props.article.date);
    
    const { status } = this.props;
    if (status === STATUS_FAILURE || status === STATUS_UNKNOWN) {
      return <Redirect to="/404" />;
    }

    if (blocs) {
      return (
        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
                <div className="col col-lg-8on9 mb-3">
                  <Breadcrumb
                    breadcrumbStyle="mt-3"
                    elements={[
                      { name: 'accueil', to: '/' },
                      { name: 'actualites', to: `/actualites` },
                      { name: label.name, to: `/actualites/${label.slug}` },
                      title
                    ]}
                  />
                </div>
                <article className="row article clearfix">
                  <ArticleHeader
                    date={date}
                    imgCaption={caption}
                    label={label.name}
                    title={title}
                    imgLink={illustration}
                    video={illustration_video}
                  />
                  <div className="col col-lg-7on9 col-lg-offset-1on9" ref={r => (this._articleContent = r)}>
                    <ArticleSocial
                      url={window.location.to}
                      title={this.props.article ? this.props.article.title : ''}
                    />
                    {this.renderTrueBlocs(blocs)}
                  </div>
                </article>
                <div className="col col-lg-7on9 col-lg-offset-1on9">
                  {tags.length > 0 && <ArticleTags tags={tags} />}
                  <FooterSocial
                    url={window.location.href}
                    title={this.props.article ? this.props.article.title : ''}
                    titleclass="mt-md-3 mt-lg-5 mb-3 ft-400"
                    clickTagManager={this.clickTagManager}
                  />
                </div>
              </section>
            </div>
            <Sidebar article={true} sidebarContent={sidebar} />
          </div>
        </div>
      );
    }

    return (
      <div className="container">
        <div className="row">
          <div className="col col-md-8 col-lg-9 bg-overflow">
            <section>
              <div className="row">
                <div className="col col-lg-8on9 mb-3">
                  <Breadcrumb
                    breadcrumbStyle="mt-3"
                    elements={[
                      { name: 'accueil', to: '/' },
                      { name: 'actualites', to: `/actualites` },
                      { name: label.name, to: `/actualites/${label.slug}` },
                      title
                    ]}
                  />
                </div>
                <article className="row article clearfix">
                  <ArticleHeader
                    date={date}
                    imgCaption={caption}
                    label={label.name}
                    title={title}
                    imgLink={illustration}
                  />
                  <div className="col col-lg-7on9 col-lg-offset-1on9">
                    <ArticleSocial />
                  </div>
                </article>
                <div className="col col-lg-7on9 col-lg-offset-1on9">
                  <ArticleTags tags={tags} />
                  <h3 className="mt-md-3 mt-lg-5 mb-3 ft-400">Partager</h3>
                  <FooterSocial clickTagManager={this.clickTagManager} />
                </div>
              </div>
            </section>
          </div>
          <Sidebar article={true} />
        </div>
      </div>
    );
  }
}

export default withHelmet(Article)((Helmet, pageProps) => (
  <Helmet>
    <title>{pageProps.article.title}</title>
    <link rel={'canonical'} href={pageProps.article.ffr_url_canonical || window.location.href} />
    <link rel={'amphtml'} href={makeAmpLink(pageProps.article.slug)} />
    <meta name={'description'} content={getFirstBlocText(pageProps.article.blocs, MAX_DESCRIPTION_LENGTH)} />
    <meta property={'og:url'} content={window.location.href} />
    <meta property={'og:title'} content={pageProps.article.title} />
    <meta property={'og:description'} content={getFirstBlocText(pageProps.article.blocs, MAX_DESCRIPTION_LENGTH)} />
    <meta property={'og:image'} content={pageProps.article.illustration} />
    <meta name={'twitter:image'} content={pageProps.article.illustration} />
  </Helmet>
));
